import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { VerifiedEntry } from '@/types/api';
import { EntryStatus } from '@/types/enums';
import { useUpdateEntry } from '@/hooks/useEntries';

interface EntryEditDialogProps {
  open: boolean;
  entry: VerifiedEntry | null;
  readOnly?: boolean;
  onClose: () => void;
}

const EntryEditDialog: React.FC<EntryEditDialogProps> = ({
  open,
  entry,
  readOnly = false,
  onClose,
}) => {
  const updateEntry = useUpdateEntry();
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [msNo, setMsNo] = useState('');
  const [status, setStatus] = useState<EntryStatus>(EntryStatus.DRAFT);
  const [notes, setNotes] = useState('');
  const [isMissing, setIsMissing] = useState(false);
  const [missingNotes, setMissingNotes] = useState('');

  useEffect(() => {
    if (entry) {
      setTitle(entry.title || '');
      setAuthor(entry.author || '');
      setMsNo(entry.ms_no || '');
      setStatus(entry.status || EntryStatus.DRAFT);
      setNotes(entry.notes || '');
      setIsMissing(entry.is_missing || false);
      setMissingNotes(entry.missing_notes || '');
    }
  }, [entry]);

  const handleSave = async () => {
    if (!entry) return;
    await updateEntry.mutateAsync({
      id: entry.id,
      data: {
        title,
        author: author || undefined,
        ms_no: msNo || undefined,
        status,
        notes: notes || undefined,
        is_missing: isMissing,
        missing_notes: missingNotes || undefined,
      },
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{readOnly ? 'Entry Details' : 'Edit Entry'}</DialogTitle>
      <DialogContent dividers>
        <TextField
          label="Title"
          fullWidth
          margin="normal"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={readOnly}
          inputProps={{ dir: 'rtl' }}
        />
        <TextField
          label="Author"
          fullWidth
          margin="normal"
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
          disabled={readOnly}
          inputProps={{ dir: 'rtl' }}
        />
        <TextField
          label="MS No"
          fullWidth
          margin="normal"
          value={msNo}
          onChange={(e) => setMsNo(e.target.value)}
          disabled={readOnly}
        />
        <FormControl fullWidth margin="normal" disabled={readOnly}>
          <InputLabel>Status</InputLabel>
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as EntryStatus)}
            label="Status"
          >
            <MenuItem value={EntryStatus.DRAFT}>Draft</MenuItem>
            <MenuItem value={EntryStatus.REVIEWED}>Reviewed</MenuItem>
            <MenuItem value={EntryStatus.PUBLISHED}>Published</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Notes"
          fullWidth
          margin="normal"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={readOnly}
          multiline
          rows={3}
        />
        <FormControlLabel
          control={
            <Switch
              checked={isMissing}
              onChange={(e) => setIsMissing(e.target.checked)}
              disabled={readOnly}
            />
          }
          label="Missing Manuscript"
        />
        {isMissing && (
          <TextField
            label="Missing Notes"
            fullWidth
            margin="normal"
            value={missingNotes}
            onChange={(e) => setMissingNotes(e.target.value)}
            disabled={readOnly}
            multiline
            rows={3}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!readOnly && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={updateEntry.isPending}
          >
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EntryEditDialog;
